import { registerLocaleData } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import {
    LOCALE_ID,
    enableProdMode,
    importProvidersFrom,
    inject,
    provideAppInitializer,
    provideZoneChangeDetection
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { provideUserManagement } from '@gbr-monorepo/user-mgmt-ui';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { NgxsModule } from '@ngxs/store';
import { ShuiEnvironment } from '@shui/core/utils/environment.js';
import { catchError, map, of, take } from 'rxjs';
import { AppComponent } from './app/app.component';
import { APP_ROUTES } from './app/app.routes';
import { provideInterceptors } from './app/auth/providers/provide-interceptors';
import { provideMsalConfiguration } from './app/auth/providers/provide-msal-configuration';
import { provideMsalServices } from './app/auth/providers/provide-msal-services';
import { UserState } from './app/auth/state/user.state';
import { ApplicationsFilterState } from './app/season/state/applications-filter/applications-filter.state';
import { SeasonFormState } from './app/season/state/season-form/season-form.state';
import { SeasonState } from './app/season/state/season/season.state';
import { TranslationsEditorState } from './app/translations/states/translations-editor.state';
import { GraphUserService } from './app/user-management/services/graph-user.service';
import { UsersState } from './app/user-management/state/users.state';
import { WorkersCouncilGroupState } from './app/workers-council-group/state/workers-council-group.state';
import { environment } from './environments/environment';

// Initialize MSAL and handle redirect callbacks
function initializeMsal(msalService: MsalService) {
    return () => {
        return msalService.handleRedirectObservable().pipe(
            map((): void => {
                // Set the active account after login
                const accounts = msalService.instance.getAllAccounts();
                if (accounts.length > 0) {
                    const activeAccount = msalService.instance.getActiveAccount() || accounts[0];
                    msalService.instance.setActiveAccount(activeAccount);
                }
                return undefined;
            }),
            catchError(error => {
                console.error('Error during MSAL initialization:', error);
                return of(undefined);
            }),
            take(1)
        );
    };
}

export function createTranslateLoader(http: HttpClient): TranslateHttpLoader {
    const fileLoc = encodeURIComponent('admin/');
    return new TranslateHttpLoader(http, `${environment.translationsUrl}/translations/${fileLoc}`, '.json');
}

registerLocaleData(localeDe);

ShuiEnvironment.setUseRedesignedIcons(true);

if (environment.production) {
    enableProdMode();
}

bootstrapApplication(AppComponent, {
    providers: [
        provideZoneChangeDetection(),
        importProvidersFrom(
            BrowserModule,
            ReactiveFormsModule,
            NgxsModule.forRoot(
                [
                    UserState,
                    SeasonState,
                    ApplicationsFilterState,
                    SeasonFormState,
                    UsersState,
                    WorkersCouncilGroupState,
                    TranslationsEditorState
                ],
                {
                    developmentMode: !environment.production,
                    compatibility: {
                        strictContentSecurityPolicy: true
                    }
                }
            ),
            NgxsFormPluginModule.forRoot(),
            NgxsReduxDevtoolsPluginModule.forRoot(),
            TranslateModule.forRoot({
                defaultLanguage: 'de',
                loader: {
                    provide: TranslateLoader,
                    useFactory: createTranslateLoader,
                    deps: [HttpClient]
                }
            })
        ),
        provideRouter(APP_ROUTES),
        provideAppInitializer(() => {
            const initializerFn = initializeMsal(inject(MsalService));
            return initializerFn();
        }),
        { provide: LOCALE_ID, useValue: 'de-DE' },
        provideHttpClient(withXhr(), withInterceptorsFromDi()),
        provideAnimations(),
        provideUserManagement(GraphUserService),
        provideMsalServices(),
        provideMsalConfiguration(),
        provideInterceptors()
    ]
}).catch(err => console.error(err));
